import React from 'react';
import * as ReactDOM from 'react-dom';

import type { UnsavedChangesModalProps } from './UnsavedChangesModal';

const UnsavedChangesModal = React.lazy(() =>
  import('./UnsavedChangesModal').then((module) => ({ default: module.UnsavedChangesModal }))
);

export interface DirtyFormStateInterface {
  isDirty: boolean;
  isSubmitting: boolean;
  submit: any;
  reset: any;
  trigger?: (
    payload?: string | string[] | undefined,
    options?: { shouldFocus?: boolean | undefined }
  ) => Promise<boolean>;
}

interface DirtyForm {
  forms: Record<string, DirtyFormStateInterface>;
  setForms: React.Dispatch<React.SetStateAction<Record<string, DirtyFormStateInterface>>>;

  toggleUnsavedChangesModal: (props?: UnsavedChangesModalProps) => void;
}

const DirtyFormContext = React.createContext<DirtyForm>({
  forms: {},
  setForms: () => {},

  toggleUnsavedChangesModal: (_props?: UnsavedChangesModalProps) => {},
});

export const useDirtyFormContext = (): DirtyForm => React.useContext(DirtyFormContext);

export const DirtyFormContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [forms, setForms] = React.useState({});
  const [unsavedChangesModalProps, setUnsavedChangesModalProps] =
    React.useState<UnsavedChangesModalProps | null>(null);

  const toggleUnsavedChangesModal = React.useCallback((props?: UnsavedChangesModalProps): void => {
    setUnsavedChangesModalProps(props ?? null);
  }, []);
  const dirtyFormContextValue = React.useMemo(
    () => ({ forms, setForms, toggleUnsavedChangesModal }),
    [forms, toggleUnsavedChangesModal]
  );

  return (
    <DirtyFormContext.Provider value={dirtyFormContextValue}>
      {children}
      {unsavedChangesModalProps
        ? ReactDOM.createPortal(
            <React.Suspense>
              <UnsavedChangesModal {...unsavedChangesModalProps} />
            </React.Suspense>,
            document.body
          )
        : null}
    </DirtyFormContext.Provider>
  );
};

export default DirtyFormContext;
